import React, { Suspense, useCallback, useContext, useEffect, useState } from "react";
import { useMemo } from "react";
import { CustomView, isMobileOnly } from "react-device-detect";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ConfirmationModal, DashboardLayout, Loader, PaymentsButton, ViewportContext } from "@monorepo/components";
import { LANGUAGE } from "@monorepo/constants";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { useActiveCartGroup, useLogout } from "@monorepo/hooks";
import { LogoutIcon, PaymentHistoryIcon, PaymentListIcon } from "@monorepo/icons";
import { useSetAllAgreementsMutation, useUnsetAllAgreementsMutation } from "@monorepo/services/api/agreements";
import { useClientInfoQuery, useClientQuery } from "@monorepo/services/api/auth";
import { useGetCartGroupsQuery } from "@monorepo/services/api/carts";
import theme, { iconsSizes } from "@monorepo/styles/theme";
import {
  DashboardPaymentActiveViewEnum,
  DashboardServiceActiveViewEnum,
  IdentityStatus,
  LanguagesEnum,
} from "@monorepo/types";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { IconButton, Typography } from "@mui/material";
import { Box } from "@mui/material";
import Button, { ButtonProps } from "@mui/material/Button";
import { styled } from "@mui/material/styles";

import { Header } from "components/Header/Header";
import { Menu } from "components/Menu/Menu";
import MessageForMobileDevices from "components/MessageForMobileDevices/MessageForMobileDevices";
import PdfUpload from "components/PdfUpload/PdfUpload";
import { useAppDispatch } from "hooks";

interface IToggleButtonProps extends ButtonProps {
  active: boolean;
}
interface IAddButtonProps {
  onClick: (e: React.MouseEvent) => void;
}
interface IDashboardInterface {
  context?: React.ReactNode;
  children?: React.ReactNode;
  hideContextMenu?: boolean;
  activePaymentView?: DashboardPaymentActiveViewEnum;
  activeServiceView?: DashboardServiceActiveViewEnum;
}

const ToggleButtons = styled("div")(() => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gridGap: 9,
}));

const AddButton = styled(Button)(() => ({
  marginBottom: theme.spacing(2),
}));

const ToggleButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})<IToggleButtonProps>(({ active }) => ({
  background: theme.palette.secondary.main,
  color: active ? theme.palette.primary.main : theme.palette.text.disabled,
  display: "inline-flex",
  [theme.breakpoints.down("md")]: {
    gap: 2,
  },
  "& path": {
    stroke: active ? theme.palette.primary.main : theme.palette.gullGray.main,
  },
  "&:hover": {
    background: active ? theme.palette.secondary.main : theme.palette.secondary.dark,
    color: active ? theme.palette.primary.main : theme.palette.text.disabled,
  },
  [theme.breakpoints.down("sm")]: {
    whiteSpace: "normal",
  },
}));

const TemplatesButton: React.FC<IAddButtonProps> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <AddButton
      variant="contained"
      disableElevation
      startIcon={<AddCircleOutlineIcon sx={{ width: 30, height: 30, fontSize: iconsSizes.small }} />}
      color="primary"
      onClick={onClick}
    >
      {t("services.new_template")}
    </AddButton>
  );
};
const RecurringPaymentButton: React.FC<IAddButtonProps> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <AddButton
      variant="contained"
      disableElevation
      startIcon={<AddCircleOutlineIcon sx={{ width: 30, height: 30, fontSize: iconsSizes.small }} />}
      color="primary"
      onClick={onClick}
    >
      {t("services.add_recurring_payment")}
    </AddButton>
  );
};

const MobileHeader = () => {
  const history = useHistory();
  const { location } = history;
  const backButtonIsDisabled = `${location.pathname}/` === routeConfig.dashboard.self;
  const { t } = useTranslation();
  const { data: client } = useClientQuery();
  const { logout } = useLogout();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        backgroundColor: theme.palette.background.paper,
        alignItems: "center",
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.geyserGray.main}`,
        position: "fixed",
        top: "max(0, env(safe-area-inset-bottom))",
        paddingRight: "env(safe-area-inset-right)",
        paddingLeft: "env(safe-area-inset-right)",
        left: 0,
        right: 0,
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <IconButton
          aria-label={t("common.back")}
          sx={{
            justifySelf: "flex-start",
            height: 50,
            display: "flex",
            justifyItems: "center",
            borderRadius: "12px",
          }}
          type="button"
          disabled={backButtonIsDisabled}
          onClick={() => history.goBack()}
        >
          <ArrowBackIosIcon fontSize="small" />
          <Typography
            component="span"
            sx={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginRight: theme.spacing(3),
              paddingY: theme.spacing(1),
            }}
          >
            {t("common.back")}
          </Typography>
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <Typography
          component="span"
          sx={{
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: theme.palette.nobelGray.main,
            marginRight: theme.spacing(3),
            paddingY: theme.spacing(1),
            "&:hover": {
              color: theme.palette.text.secondary,
              background: "transparent",
              cursor: "pointer",
              borderRadius: "5px",
            },
          }}
          onClick={() => history.push(routeConfig.dashboard.profile.personal)}
        >
          {client?.name} {client?.surname}
        </Typography>
        <IconButton
          sx={{ justifySelf: "flex-end", width: 50, height: 50, display: "flex", justifyItems: "center" }}
          onClick={logout}
        >
          <LogoutIcon
            sx={{
              display: "inherit",
              fontSize: iconsSizes.large,
              stroke: theme.palette.nobelGray.main,
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

const ServiceViewToggle: React.FC<{ activeKey: DashboardServiceActiveViewEnum }> = ({ activeKey }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const options = useMemo(
    () => [
      {
        path: routeConfig.dashboard.services.templates.self,
        key: DashboardServiceActiveViewEnum.TEMPLATES,
        name: t("services.templates"),
      },
      {
        path: routeConfig.dashboard.services.recurringPayments.self,
        key: DashboardServiceActiveViewEnum.RECURRING_PAYMENTS,
        name: t("services.recurring_payments"),
      },
    ],
    [t]
  );
  return (
    <ToggleButtons>
      {options?.map(
        ({ path, key, name, icon }: { path: string; key: string; name: string; icon?: React.ReactNode }) => (
          <ToggleButton
            key={key}
            sx={{ minWidth: "unset" }}
            type="button"
            variant="contained"
            disableElevation
            onClick={() => history.push(path)}
            active={key === activeKey}
            startIcon={icon}
          >
            {name}
          </ToggleButton>
        )
      )}
    </ToggleButtons>
  );
};

const PaymentViewToggle: React.FC<{ activeKey: DashboardPaymentActiveViewEnum }> = ({ activeKey }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { isMobile } = useContext(ViewportContext);
  const { data: cartGroups, isLoading: isCartGroupsLoading } = useGetCartGroupsQuery();

  const options = useMemo(() => {
    return [
      {
        eventName: "payment_list",
        path: routeConfig.dashboard.payments.self,
        key: DashboardPaymentActiveViewEnum.CART_LIST,
        name: t("payments.payment_list"),
        icon: <PaymentListIcon />,
        disabled: isCartGroupsLoading,
      },
      {
        eventName: "history",
        path: routeConfig.dashboard.payments.history.self,
        key: DashboardPaymentActiveViewEnum.HISTORY,
        name: t("payment_history.label_full"),
        icon: <PaymentHistoryIcon />,
        disabled: isCartGroupsLoading,
      },
    ];
  }, [t, isMobile, cartGroups, isCartGroupsLoading]);

  return (
    <ToggleButtons>
      {options?.map(({ path, key, name, eventName, icon, disabled }) => (
        <ToggleButton
          sx={{
            fontSize: "1rem",
          }}
          key={key}
          type="button"
          variant="contained"
          disableElevation
          onClick={() => {
            ReactGA.event(eventName);
            history.push(path);
          }}
          active={key === activeKey}
          startIcon={icon}
          disabled={disabled}
        >
          {name}
        </ToggleButton>
      ))}
    </ToggleButtons>
  );
};

export const Dashboard: React.FC<IDashboardInterface> = ({
  context,
  children,
  hideContextMenu,
  activePaymentView,
  activeServiceView,
}) => {
  const { isMobile } = useContext(ViewportContext);
  const [showModal, setShowModal] = useState<boolean>(false);
  useAppDispatch();
  const history = useHistory();
  const { data: client } = useClientQuery();
  const { data: clientInfo, refetch } = useClientInfoQuery({ skipInitialLoad: true });
  const [showAgreementModal, setShowAgreementModal] = useState<boolean>(clientInfo?.agreements_outdated || false);
  const [unsetAllAgreements] = useUnsetAllAgreementsMutation();
  const [setAllAgreements] = useSetAllAgreementsMutation();
  const { activeCartGroup, isLoading } = useActiveCartGroup();
  const isPaymentButtonDisabled = isLoading || (!activeCartGroup?.is_owner && !Boolean(activeCartGroup?.add));
  const identityUnverified =
    client?.identity_status === IdentityStatus.UNVERIFIED || client?.identity_status === IdentityStatus.PENDING;
  const { t } = useTranslation();

  useEffect(() => {
    setShowAgreementModal(!!clientInfo?.agreements_outdated);
  }, [clientInfo?.agreements_outdated]);

  const isPLCartOrHistory =
    LANGUAGE === LanguagesEnum.PL &&
    (activePaymentView === DashboardPaymentActiveViewEnum.CART_LIST ||
      activePaymentView === DashboardPaymentActiveViewEnum.HISTORY);

  const hideModalAndRefetchClientInfo = useCallback(() => {
    setShowAgreementModal(false);
    refetch();
  }, [refetch]);

  return (
    <>
      <DashboardLayout
        hideContextMenu={hideContextMenu}
        menu={<Menu />}
        header={isMobile ? <MobileHeader /> : <Header />}
        activePaymentView={activePaymentView}
        context={
          <Suspense fallback={<Loader />}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                marginBottom: 2,
                width: "100%",
              }}
            >
              {isMobile ||
              activeServiceView === DashboardServiceActiveViewEnum.TEMPLATES ||
              activeServiceView === DashboardServiceActiveViewEnum.RECURRING_PAYMENTS ? null : LANGUAGE !==
                  LanguagesEnum.PL || isPLCartOrHistory ? (
                <>
                  <PaymentsButton disabled={isPaymentButtonDisabled} eventName={"new_service"} />
                  {isPLCartOrHistory && <PdfUpload />}
                </>
              ) : null}
              {isMobile || activeServiceView !== DashboardServiceActiveViewEnum.TEMPLATES ? null : (
                <TemplatesButton
                  onClick={() => {
                    if (identityUnverified) {
                      setShowModal(true);
                    } else {
                      history.push(routeConfig.dashboard.services.templates.addTemplate.self);
                    }
                  }}
                />
              )}
              {isMobile || activeServiceView !== DashboardServiceActiveViewEnum.RECURRING_PAYMENTS ? null : (
                <RecurringPaymentButton
                  onClick={() => {
                    if (identityUnverified) {
                      setShowModal(true);
                    } else {
                      history.push(routeConfig.dashboard.services.recurringPayments.addRecurringPayment.self);
                    }
                  }}
                />
              )}
              {!!activePaymentView && activePaymentView !== DashboardPaymentActiveViewEnum.SERVICES ? (
                <PaymentViewToggle activeKey={activePaymentView} />
              ) : null}
              {!!activeServiceView ? <ServiceViewToggle activeKey={activeServiceView} /> : null}
            </Box>
            {context || null}
          </Suspense>
        }
      >
        {children ? <Suspense fallback={<Loader />}>{children}</Suspense> : null}
        <CustomView condition={isMobileOnly}>
          <MessageForMobileDevices />
        </CustomView>
      </DashboardLayout>
      {showModal && (
        <ConfirmationModal open={showModal} onClose={() => setShowModal(false)}>
          {t("services.need_to_verify_identity")}
          {t("services.authentication_info")}
        </ConfirmationModal>
      )}
      {showAgreementModal && (
        <ConfirmationModal
          open={showAgreementModal}
          onAction={() => {
            setAllAgreements()
              .unwrap()
              .then(() => hideModalAndRefetchClientInfo());
          }}
          onDecline={() => {
            unsetAllAgreements()
              .unwrap()
              .then(() => hideModalAndRefetchClientInfo());
          }}
        >
          {t("agreements.need_to_renew")}
          {t("register.agree_notifications_about_invoices_newsletters_and_offers_label")}
        </ConfirmationModal>
      )}
    </>
  );
};
